import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Plus, Trophy, Eye, Users, MapPin, ArrowRight } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import ListState from '../components/ListState';
import CategoryBadges from '../components/CategoryBadges';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrganizerDashboard = () => {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTournaments = async () => {
      try {
        const { data } = await axios.get(`${API}/organizer/tournaments`, { withCredentials: true });
        setTournaments(data);
        const results = await Promise.all(
          data.map(t => axios.get(`${API}/organizer/tournaments/${t.tournament_id}/analytics`, { withCredentials: true })
            .then(r => ({ id: t.tournament_id, data: r.data })).catch(() => null))
        );
        const map = {};
        results.forEach(a => { if (a) map[a.id] = a.data; });
        setAnalytics(map);
      } catch (e) {
        logError('Failed to load organizer tournaments:', e);
      } finally {
        setLoading(false);
      }
    };
    loadTournaments();
  }, []);

  const statusBadge = (s) => {
    const map = {
      pending_approval: 'bg-orange-50 text-orange-700 border-orange-200',
      approved: 'bg-green-50 text-green-700 border-green-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
      deletion_requested: 'bg-red-50 text-red-700 border-red-200',
      archived: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return map[s] || 'bg-gray-50 text-gray-700 border-gray-200';
  };
  const statusLabel = (s) => (s || '').replace('_', ' ');

  const totalRegs = Object.values(analytics).reduce((sum, a) => sum + (a.total_registrations || 0), 0);
  const totalViews = Object.values(analytics).reduce((sum, a) => sum + (a.views || 0), 0);

  return (
    <>
      <SEO title="Organizer Dashboard" description="Manage your basketball tournaments, view registrations, and track analytics on Beyond Ball India." path="/organizer" />
      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 animate-fade-up">
            <div>
              <p className="text-gray-500 text-sm mb-1">Welcome back,</p>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900" data-testid="organizer-dashboard-title">
                {user?.name}
              </h1>
            </div>
            <Link to="/organizer/create-tournament" className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm" data-testid="create-tournament-btn">
              <Plus size={18} weight="bold" /> Create Tournament
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-fade-up delay-100">
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-2xl sm:text-3xl font-bold text-gray-900">{tournaments.length}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Tournaments</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-2xl sm:text-3xl font-bold text-green-600">{tournaments.filter(t => t.status === 'approved').length}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Approved</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-2xl sm:text-3xl font-bold text-gray-900">{totalRegs}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Registrations</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <div className="text-2xl sm:text-3xl font-bold text-[#F97316]">{totalViews}</div>
              <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Total Views</div>
            </div>
          </div>

          {/* Tournaments */}
          <h2 className="font-bold text-gray-900 text-lg mb-4 animate-fade-up delay-200">My Tournaments</h2>

          <ListState
            loading={loading}
            empty={tournaments.length === 0}
            skeleton={
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1,2,3].map(i => <div key={i} className="bg-white rounded-2xl border border-gray-200 h-64 skeleton"></div>)}
              </div>
            }
            emptyState={
              <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
                <Trophy size={48} className="text-gray-300 mx-auto mb-4" weight="duotone" />
                <p className="text-gray-900 font-semibold mb-1">No tournaments yet</p>
                <p className="text-gray-500 text-sm mb-5">Create your first tournament to get started.</p>
                <Link to="/organizer/create-tournament" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm">
                  <Plus size={16} weight="bold" /> Create Tournament
                </Link>
              </div>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-up delay-300">
              {tournaments.map((t, i) => {
                const a = analytics[t.tournament_id];
                return (
                  <div key={t.tournament_id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden card-lift" data-testid={`tournament-card-${i}`}>
                    <div className="relative aspect-[16/10] bg-gradient-to-br from-orange-100 to-orange-50">
                      {t.banner_url ? (
                        <img src={t.banner_url} alt={t.name} className="w-full h-full object-cover" style={{ objectPosition: `${t.banner_focus_x ?? 50}% ${t.banner_focus_y ?? 50}%` }} loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-5xl font-bold text-orange-200">{t.format}</span>
                        </div>
                      )}
                      <div className={`absolute top-3 left-3 text-[10px] px-2.5 py-1 rounded-full border font-semibold uppercase ${statusBadge(t.status)}`}>
                        {t.status.replace('_', ' ')}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">{t.name}</h3>
                      <CategoryBadges ages={t.age_categories || []} genders={t.gender_categories || []} className="mb-2" />
                      <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-4">
                        <MapPin size={14} weight="bold" /> {t.city}, {t.state}
                      </div>

                      {a && (
                        <div className="grid grid-cols-3 gap-2 mb-4 pt-3 border-t border-gray-100">
                          <div className="text-center">
                            <div className="text-sm font-bold text-gray-900 flex items-center justify-center gap-1"><Eye size={12} />{a.views}</div>
                            <div className="text-[10px] text-gray-500 uppercase font-semibold mt-0.5">Views</div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm font-bold text-gray-900 flex items-center justify-center gap-1"><Users size={12} />{a.total_registrations}</div>
                            <div className="text-[10px] text-gray-500 uppercase font-semibold mt-0.5">Regs</div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm font-bold text-green-600">{a.approved}</div>
                            <div className="text-[10px] text-gray-500 uppercase font-semibold mt-0.5">Approved</div>
                          </div>
                        </div>
                      )}

                      <div className="space-y-1.5">
                        <Link to={`/organizer/tournaments/${t.tournament_id}/registrations`} className="block text-center bg-gray-900 hover:bg-gray-800 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors">
                          Manage Registrations
                        </Link>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Link to={`/organizer/tournaments/${t.tournament_id}/edit`} className="text-center bg-gray-50 hover:bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold">Edit</Link>
                          <Link to={`/tournaments/${t.tournament_id}`} className="text-center bg-gray-50 hover:bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold">View</Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ListState>
        </div>
      </div>
    </>
  );
};

export default OrganizerDashboard;
